import axios from 'axios';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';

class OrganizationRoleService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  /**
   * Get authorization headers
   * @returns {Object} Headers with auth token
   */
  getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  /**
   * Get all role assignments for an organization
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Object>} Role assignments
   */
  async getOrganizationRoles(organizationId) {
    try {
      const response = await axios.get(
        `${this.baseURL}${API_ENDPOINTS.ORGANIZATIONS.ROLES(organizationId)}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching organization roles:', error);
      throw error;
    }
  }

  /**
   * Assign a user to a role in an organization
   * @param {string} organizationId - Organization ID
   * @param {string} userId - User ID
   * @param {string} roleType - Role type (account-manager, support-responsible)
   * @returns {Promise<Object>} Assignment result
   */
  async assignUserToRole(organizationId, userId, roleType) {
    try {
      const response = await axios.post(
        `${this.baseURL}${API_ENDPOINTS.ORGANIZATIONS.ROLES(organizationId)}`,
        { userId, roleType },
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error assigning user to role:', error);
      throw error;
    }
  }

  /**
   * Remove a user from a role in an organization
   * @param {string} organizationId - Organization ID
   * @param {string} userId - User ID
   * @param {string} roleType - Role type
   * @returns {Promise<Object>} Removal result
   */
  async removeUserFromRole(organizationId, userId, roleType) {
    try {
      const response = await axios.delete(
        `${this.baseURL}${API_ENDPOINTS.ORGANIZATIONS.ROLES(organizationId)}/${userId}/${roleType}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error removing user from role:', error);
      throw error;
    }
  }

  /**
   * Get users with a specific role in an organization
   * @param {string} organizationId - Organization ID
   * @param {string} roleType - Role type
   * @returns {Promise<Array>} Users with the role
   */
  async getUsersByRole(organizationId, roleType) {
    try {
      const response = await axios.get(
        `${this.baseURL}${API_ENDPOINTS.ORGANIZATIONS.ROLES_BY_TYPE(organizationId, roleType)}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching users by role:', error);
      throw error;
    }
  }

  /**
   * Check if a user has a specific role in an organization
   * @param {string} organizationId - Organization ID
   * @param {string} userId - User ID
   * @param {string} roleType - Role type
   * @returns {Promise<boolean>} True if user has the role
   */
  async checkUserRole(organizationId, userId, roleType) {
    try {
      const response = await axios.get(
        `${this.baseURL}${API_ENDPOINTS.ORGANIZATIONS.ROLES_CHECK(organizationId, userId, roleType)}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data.data.hasRole;
    } catch (error) {
      console.error('Error checking user role:', error);
      return false;
    }
  }

  /**
   * Get role statistics for an organization
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Object>} Role statistics
   */
  async getRoleStats(organizationId) {
    try {
      const response = await axios.get(
        `${this.baseURL}${API_ENDPOINTS.ORGANIZATIONS.ROLES_STATS(organizationId)}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching role stats:', error);
      throw error;
    }
  }

  /**
   * Get all role assignments for a specific user
   * @param {string} userId - User ID
   * @returns {Promise<Object>} User's role assignments
   */
  async getUserOrganizationRoles(userId) {
    try {
      const response = await axios.get(
        `${this.baseURL}${API_ENDPOINTS.USERS.ORGANIZATION_ROLES(userId)}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching user organization roles:', error);
      throw error;
    }
  }

  /**
   * Get all users with a specific role across all organizations
   * @param {string} roleType - Role type
   * @returns {Promise<Array>} All users with the role
   */
  async getAllUsersWithRole(roleType) {
    try {
      const response = await axios.get(
        `${this.baseURL}${API_ENDPOINTS.ORGANIZATION_ROLES.ALL_USERS_WITH_ROLE(roleType)}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching all users with role:', error);
      throw error;
    }
  }
}

// Export singleton instance
export default new OrganizationRoleService();
